import { faCopy } from '@fortawesome/free-solid-svg-icons'
import { faRefresh } from '@fortawesome/pro-solid-svg-icons'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { CopyText, LabelContent } from 'V2Components'
import { getResourceName } from 'features/resources'
import { UpdateWorkloadKeysModal } from 'features/workloads'
import moment from 'moment'
import { Button, IconButton, Typography } from 'procyon-ui'
import { useDataDetailsContext } from 'providers/DataDetailsProvider'
import React, { useEffect, useRef, useState } from 'react'
import { ManageWorkloadAccounts } from '../ManageWorkloadAccounts'
import { reduxApiClient } from 'infra/dataFetching'

const POLL_DELAY_MS = 3000

const OverviewTab = () => {
  const [showUpdateKeysModal, setShowUpdateKeysModal] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)

  const [workloadCredentials, setWorkloadCredentials] = useState({
    key: '',
    secret: '',
    notAfter: ''
  })

  const workload = useDataDetailsContext()
  const { selectDispatch, getObjectRef } = useMultiSlice(['userList'])
  const createdBy = getObjectRef(workload.CreatedBy)

  const previousSecretRef = useRef(null)

  // Track previous secret to detect rotation
  useEffect(() => {
    if (workload?.Status?.ClientSecret) {
      previousSecretRef.current = workload.Status.ClientSecret
    }
  }, [workload?.Status?.ClientSecret])

  // Sync credentials when workload updates
  useEffect(() => {
    if (workload?.Status?.ClientId && workload?.Status?.ClientSecret) {
      setWorkloadCredentials({
        key: workload.Status.ClientId,
        secret: workload.Status.ClientSecret,
        notAfter: workload.Status.NotAfter
      })
      setIsRegenerating(false)
    }
  }, [workload?.Status?.ClientId, workload?.Status?.ClientSecret, workload?.Status?.NotAfter])

  // Poll BE until NEW secret is available
  const pollForUpdatedKeys = () => {
    if (!workload?.ObjectMeta?.Name) return

    setIsRegenerating(true)

    setTimeout(() => {
      reduxApiClient('workloadidentitys').startRsrcObjectPolling(
        workload.ObjectMeta.Name,
        (obj) => {
          const newSecret = obj?.Status?.ClientSecret

          if (newSecret && newSecret !== previousSecretRef.current) {
            setWorkloadCredentials({
              key: obj.Status.ClientId,
              secret: newSecret,
              notAfter: obj.Status.NotAfter
            })
            setIsRegenerating(false)
            return true
          }
          return false
        },
        `workloadidentitys/${workload.ObjectMeta.Name}`
      )
    }, POLL_DELAY_MS)
  }

  useEffect(() => {
    selectDispatch(['accountList', 'gcpResources', 'projects', 'userList', 'workloads'])
  }, [])

  return (
    <div className='p-4'>
      <div className='flex gap-4'>
        <div className='w-1/2 border-r border[#D8DDE4] pr-8'>
          <Typography className='mb-8' variant='h4-regular'>
            Workload Information
          </Typography>

          <LabelContent title='Name' content={getResourceName(workload)} />
          <LabelContent title='Description' content={workload.Spec.Description} />
          <LabelContent title='Creator' content={getResourceName(createdBy)} showEmptyLabel />
          <LabelContent
            title='Created On'
            content={moment(workload.ObjectMeta.CreatedAt).format('MMMM Do YYYY, h:mm A')}
            showEmptyLabel
          />

          <Typography className='my-8' variant='h4-regular'>
            For Developers
          </Typography>

          {/* API Key – NO loading */}
          <LabelContent
            title='API Key'
            content={
              <CopyText
                showText
                messageKey='API Key'
                text={workloadCredentials.key || '—'}
                Component={
                  <IconButton
                    disabled={!workloadCredentials.key || isRegenerating}
                    size='sm'
                    icon={faCopy}
                    variant='gray'
                  />
                }
              />
            }
          />

          {/* API Secret – loading only here */}
          <LabelContent
            title='API Secret'
            content={
              <div className='flex gap-4 justify-between items-center'>
                {isRegenerating
                  ? 'Regenerating secret…'
                  : workloadCredentials.secret
                    ? '********** ********** ********** **********'
                    : '—'}
                <CopyText
                  showText={false}
                  messageKey='API Secret'
                  text={workloadCredentials.secret}
                  Component={
                    <IconButton
                      disabled={!workloadCredentials.secret || isRegenerating}
                      size='sm'
                      icon={faCopy}
                      variant='gray'
                    />
                  }
                />
              </div>
            }
          />

          {/* Expiry – NO loading */}
          <LabelContent
            title='Expires'
            content={
              workloadCredentials.notAfter
                ? moment(workloadCredentials.notAfter).format('MMMM Do YYYY, h:mm A')
                : '—'
            }
          />

          <div className='mt-4 flex justify-end'>
            <Button
              disabled={!workloadCredentials.key || !workloadCredentials.secret || isRegenerating}
              onClick={() => setShowUpdateKeysModal(true)}
              variant='gray'
              icon={faRefresh}
            >
              {isRegenerating ? 'Updating…' : 'Update Keys'}
            </Button>
          </div>
        </div>

        <div className='w-1/2'>
          <ManageWorkloadAccounts workload={workload} />
        </div>
      </div>

      {showUpdateKeysModal && (
        <UpdateWorkloadKeysModal
          workload={workload}
          onCancel={() => setShowUpdateKeysModal(false)}
          cleanUpFn={() => {
            setShowUpdateKeysModal(false)
            pollForUpdatedKeys()
          }}
        />
      )}
    </div>
  )
}

export { OverviewTab }
